// Renders a country flag as a self-hosted SVG image rather than a native emoji.
//
// Why not the 🇨🇦 emoji directly: Windows' emoji font (Segoe UI Emoji, used by
// Chrome/Edge on Windows) ships NO country-flag glyphs, so a flag emoji falls
// back to the bare 2-letter code ("CA"). No CSS font-family change fixes this —
// the glyph simply doesn't exist on Windows. An SVG image renders the flag
// identically on every platform (Windows included) and is font-independent, so
// it also renders deterministically in tests.
//
// The SVGs are bundled in /public/flags (Twemoji artwork) — self-hosted, no
// external CDN, works offline. Add a new flag there to support a new country.
// Falls back to the 🌐 globe emoji for an unknown/unbundled code.
import { countryFlag } from "@shared/constants/provinces";

/** Country codes with a bundled flag SVG in /public/flags. */
const BUNDLED_FLAGS = new Set(["CA", "US", "GB", "AU"]);

export function FlagIcon({ country, className = "" }: { country?: string | null; className?: string }) {
  const code = (country ?? "").toUpperCase();
  if (!BUNDLED_FLAGS.has(code)) {
    return <span className={className} aria-hidden="true">{countryFlag(country)}</span>;
  }
  return (
    <img
      src={`/flags/${code.toLowerCase()}.svg`}
      alt={code}
      title={code}
      draggable={false}
      loading="lazy"
      className={`inline-block h-[1em] w-auto align-[-0.125em] ${className}`}
    />
  );
}
